import React, { useEffect, useMemo, useState } from 'react';

import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Button, Checkbox, FormControlLabel, FormGroup, Typography, Divider } from '@mui/material';
import { GridCloseIcon } from '@mui/x-data-grid';

export interface DialogSelectableItem {
  id: string;
  label: string;
}

interface DialogConfirmSelectableProps {
  open: boolean;
  title: string;
  contentText: string;
  confirmButtonText: string;
  onClose: () => void;
  onConfirm: (selectedIds: string[]) => void;
  selectableItems?: DialogSelectableItem[];
  selectionTitle?: string;
  selectAllLabel?: string;
}

const DialogConfirmSelectable: React.FC<DialogConfirmSelectableProps> = ({
  open,
  title,
  contentText,
  confirmButtonText,
  onClose,
  onConfirm,
  selectableItems,
  selectionTitle,
  selectAllLabel,
}) => {
  const items = useMemo(() => selectableItems ?? [], [selectableItems]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setSelectedIds([]);
    }
  }, [open]);

  const allSelected = items.length > 0 && selectedIds.length === items.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < items.length;

  const handleToggleAll = () => {
    setSelectedIds(allSelected ? [] : items.map((item) => item.id));
  };

  const handleToggleItem = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      // Prevent closing when clicking on the backdrop
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="dialog-confirm-selectable-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="dialog-confirm-selectable-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {title}
        <IconButton aria-label="Sluiten" onClick={onClose} size="small">
          <GridCloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{contentText}</DialogContentText>

        {items.length > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600 }}>
              {selectionTitle ?? 'Selecteer items'}
            </Typography>
            <FormGroup>
              {items.length > 1 && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={handleToggleAll}
                      />
                    }
                    label={selectAllLabel ?? 'Alles selecteren'}
                  />
                  <Divider />
                </>
              )}
              {items.map((item) => (
                <FormControlLabel
                  key={item.id}
                  control={
                    <Checkbox
                      checked={selectedIds.includes(item.id)}
                      onChange={() => handleToggleItem(item.id)}
                    />
                  }
                  label={item.label}
                />
              ))}
            </FormGroup>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => onConfirm(selectedIds)}
        >
          {confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogConfirmSelectable;
